import React from "react";
import { useParams, Link } from "react-router-dom";
import { Seo, SITE_URL } from "@/components/Seo";
import { Breadcrumbs, crumbLd } from "@/components/Breadcrumbs";
import { getGuide, GUIDES } from "@/lib/guides";
import { ArrowRight, BookOpen } from "lucide-react";

export default function GuideArticle() {
  const { slug } = useParams();
  const guide = getGuide(slug);

  if (!guide) {
    return (
      <div className="px-4 max-w-3xl mx-auto py-24 text-center" data-testid="guide-not-found">
        <p className="text-slate-500">Guide not found.</p>
        <Link to="/guides" className="text-emerald-700 font-semibold mt-3 inline-block">Back to all guides</Link>
      </div>
    );
  }

  const crumbs = [{ name: "Home", to: "/" }, { name: "Guides", to: "/guides" }, { name: guide.h1 }];
  const others = GUIDES.filter((g) => g.slug !== slug).slice(0, 3);

  const articleLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: guide.h1,
    description: guide.description,
    author: { "@type": "Organization", name: "Dotlands Realty" },
    publisher: { "@type": "Organization", name: "Dotlands Realty", logo: { "@type": "ImageObject", url: `${SITE_URL}/logo.png` } },
    mainEntityOfPage: `${SITE_URL}/guides/${guide.slug}`,
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 max-w-3xl mx-auto py-10" data-testid="guide-article">
      <Seo title={guide.title} description={guide.description} path={`/guides/${guide.slug}`} type="article" jsonLd={[articleLd, crumbLd(crumbs)]} />
      <Breadcrumbs items={crumbs} />
      <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#C8A24C]/15 text-[#A8863A] text-xs font-bold uppercase tracking-wide"><BookOpen className="w-3.5 h-3.5" /> Guide</span>
      <h1 className="text-3xl sm:text-4xl font-heading font-extrabold text-[#0E3B2E] mt-4 leading-tight">{guide.h1}</h1>
      <p className="text-slate-600 mt-3 text-lg leading-relaxed">{guide.excerpt}</p>

      <article className="mt-8 space-y-8">
        {guide.sections.map((s, i) => (
          <section key={i}>
            <h2 className="font-heading font-bold text-xl text-[#0E3B2E]">{s.h2}</h2>
            <p className="text-slate-700 mt-2 leading-relaxed">{s.body}</p>
          </section>
        ))}
      </article>

      {guide.related?.length > 0 && (
        <div className="mt-10 flex flex-wrap gap-3">
          {guide.related.map((r) => (
            <Link key={r.to} to={r.to} className="inline-flex items-center gap-1.5 bg-[#0E3B2E] text-white text-sm font-semibold px-4 py-2.5 rounded-full hover:bg-[#0A2A20]">
              {r.label} <ArrowRight className="w-4 h-4" />
            </Link>
          ))}
        </div>
      )}

      <div className="mt-12 border-t border-slate-200 pt-8">
        <h2 className="font-heading font-bold text-lg text-slate-900 mb-4">More guides</h2>
        <ul className="space-y-2">
          {others.map((g) => (
            <li key={g.slug}><Link to={`/guides/${g.slug}`} className="text-emerald-700 hover:underline">{g.h1}</Link></li>
          ))}
        </ul>
      </div>

      <p className="text-xs text-slate-400 mt-10">This guide is general educational information and not legal advice. Always verify documents and approvals independently before any property transaction.</p>
    </div>
  );
}
